/**
 * Fallback per i comandi del display auto quando la pagina di registrazione
 * NON è montata (Ott 2026).
 *
 * PERCHÉ ESISTE: i comandi in auto vengono consumati in modo distruttivo dal
 * nativo. Se l'app sul telefono non era aperta (o la WebView è stata ricreata
 * dal sistema) la pagina di registrazione non può eseguirli: senza fallback il
 * comando andava perso e restavano effetti zombie (es. "Annulla registrazione"
 * con il servizio GPS ancora attivo e la sessione ancora in IndexedDB).
 *
 * Qui vive SOLO l'annullamento: il salvataggio non si può fare senza la pagina
 * (serve distanza, metadati e upload), quindi per "Ferma e salva" l'App naviga
 * alla pagina di registrazione con `?carSave=1`, che recupera la sessione da
 * IndexedDB e completa il salvataggio.
 */

import { stopNativeWatch } from "@/services/nativeGeolocation";
import {
  setCarRecordingState,
  stopCarLocationRelay,
} from "@/services/carBridge";
import { deleteSession, resumeSession } from "@/services/gpsTrackPersistence";
import { syncCarSnapshotNow } from "@/hooks/useCarAutoSync";

/**
 * "Annulla registrazione" toccato in auto con la pagina di registrazione non
 * montata: ferma il watch nativo e la sentinella della posizione, cancella la
 * sessione persistita e allinea il display del veicolo. Best-effort: nessuna
 * eccezione verso chi chiama.
 */
export async function discardRecordingOutsidePage(): Promise<void> {
  await stopNativeWatch().catch(() => {});
  await stopCarLocationRelay().catch(() => {});
  try {
    const session = await resumeSession();
    if (session) await deleteSession(session.sessionId);
  } catch {
    // la sessione persistita non deve bloccare l'annullamento
  }
  setCarRecordingState(null);
  await syncCarSnapshotNow().catch(() => {});
}
